import { TemplateRef } from '@angular/core';
import { SortDirection } from '@nx-vigil/core/web/shared/util';

export enum ColumnType {
  Text = 'text',
  Toggle = 'toggle',
  Template = 'template',
}

export const defaultTableOption: TableOption = {
  showPaginator: false,
  tableClass: '',
  tableHeaderClass: '',
  selectRow: false,
  pageSize: 10,
  sortable: true,
  serverSort: false,
  reorderable: false,
};

export type TableSortEvent = {
  field: string;
  direction: SortDirection;
};

export type TableColumnOption<T> =
  | ToggleColumnOption<T>
  | TextColumnOption<T>
  | TemplateColumnOption<T>;

export interface TableOption<T = unknown> {
  showPaginator?: boolean;
  tableClass?: string;
  tableHeaderClass?: string;
  selectRow?: boolean;
  pageSize?: number;
  expandTemplate?: TemplateRef<unknown>;
  dataKey?: string;
  emptyMessage?: string;
  sortable?: boolean;
  serverSort?: boolean;
  reorderable?: boolean;
  rowActions?: RowAction<T>[];
}

export interface RowAction<T> {
  id: string;
  label: string;
  icon?: string;
  materialIcon?: string;
  visible?: (row: T) => boolean;
  disabled?: (row: T) => boolean;
  execute: (row: T) => void;
}

export interface ToggleFieldDefinition<T> {
  field: keyof T;
  icon: string;
  iconDisabled?: string;
}

export interface ColumnOptionBase<T> {
  type?: ColumnType;
  field?: keyof T | string;
  getFieldValue?: (row: T) => number | string | T[keyof T];
  disabled?: boolean;
  header?: string | TemplateRef<unknown>;
  headerClass?: string;
  dataClass?: string;
  frozen?: boolean;
  filterable?: boolean;
}

export interface TextColumnOption<T> extends ColumnOptionBase<T> {
  dateFormat?: string;
  timezone?: string;
  decimalPlaces?: number;
  currency?: 'USD' | 'EUR' | 'GBP';
}

export interface ToggleColumnOption<T> extends ColumnOptionBase<T> {
  columns: ToggleFieldDefinition<T>[];
}

export interface TemplateColumnOption<T> extends ColumnOptionBase<T> {
  rowTemplate: TemplateRef<unknown>;
}

export function isTemplateColumnOption<T>(
  option: ColumnOptionBase<T>,
): option is TemplateColumnOption<T> {
  return option.type === ColumnType.Template;
}

export function isToggleColumnOption<T>(
  option: ColumnOptionBase<T>,
): option is ToggleColumnOption<T> {
  return option.type === ColumnType.Toggle;
}

export function isTextColumnOption<T>(
  option: ColumnOptionBase<T>,
): option is TextColumnOption<T> {
  return option.type === ColumnType.Text;
}
